import app from 'flarum/forum/app';

/** Events: shared by the list, the event page, the form and the tag-page block. */
export const EVENTS_ROUTE = 'voucha-community.events';
export const EVENT_ROUTE = 'voucha-community.event';
export const NEW_EVENT_ROUTE = 'voucha-community.events.new';
export const api = path => app.forum.attribute('apiUrl') + '/voucha-community/events' + (path || '');
function routeName() {
  try {
    return app.current && app.current.get('routeName') || '';
  } catch (e) {
    return '';
  }
}
const path = () => {
  try {
    return (m.route.get() || '').split('?')[0];
  } catch (e) {
    return '';
  }
};
export const onEvents = () => routeName() === EVENTS_ROUTE || path() === '/events';
export const onNewEvent = () => routeName() === NEW_EVENT_ROUTE || path() === '/events/new';
export const onEvent = () => routeName() === EVENT_ROUTE || /^\/events\/\d+$/.test(path());
export const onAnyEvents = () => onEvents() || onNewEvent() || onEvent();

/** Parts of a time in the event's own time zone */
function parts(iso, tz, opts) {
  try {
    return new Intl.DateTimeFormat('en-GB', Object.assign({
      timeZone: tz
    }, opts)).format(new Date(iso));
  } catch (e) {
    return new Intl.DateTimeFormat('en-GB', opts).format(new Date(iso));
  }
}

/** "SAT" / "14" / "NOV" for the date badge */
export function badge(e) {
  return {
    day: parts(e.startsAt, e.timezone, {
      weekday: 'short'
    }).toUpperCase(),
    date: parts(e.startsAt, e.timezone, {
      day: 'numeric'
    }),
    month: parts(e.startsAt, e.timezone, {
      month: 'short'
    }).toUpperCase()
  };
}
const ZONE = {
  'Africa/Lagos': 'WAT',
  'Europe/London': 'UK time',
  'America/Toronto': 'Eastern',
  'America/Chicago': 'Central',
  'America/Edmonton': 'Mountain',
  'America/Los_Angeles': 'Pacific'
};

/** "Saturday 14 November, 18:00 – 21:00 WAT" (in the event's time zone) */
export function whenText(e, long) {
  const day = parts(e.startsAt, e.timezone, long ? {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  } : {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  const start = parts(e.startsAt, e.timezone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  let end = '';
  if (e.endsAt) {
    const sameDay = parts(e.startsAt, e.timezone, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }) === parts(e.endsAt, e.timezone, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
    end = ' – ' + (sameDay ? '' : parts(e.endsAt, e.timezone, {
      day: 'numeric',
      month: 'short'
    }) + ', ') + parts(e.endsAt, e.timezone, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }
  return day + ', ' + start + end + ' ' + (ZONE[e.timezone] || e.timezone.split('/').pop().replace(/_/g, ' '));
}

/** The reader's own time, when it differs from the event's ("Your time: 13:00") */
export function localText(e) {
  let mine = '';
  try {
    mine = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {}
  if (!mine || mine === e.timezone) return null;
  const a = parts(e.startsAt, e.timezone, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    day: 'numeric'
  });
  const b = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    day: 'numeric'
  }).format(new Date(e.startsAt));
  if (a === b) return null;
  return 'Your time: ' + new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(new Date(e.startsAt));
}
export function whereText(e) {
  if (e.format === 'online') return 'Online';
  const place = [e.venue, e.placeName].filter(Boolean).join(', ');
  return e.format === 'hybrid' ? place + ' and online' : place;
}

/** An .ics file for calendars */
export function icsHref(e) {
  const stamp = iso => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const esc = s => String(s || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\;');
  const end = e.endsAt || new Date(new Date(e.startsAt).getTime() + 2 * 3600e3).toISOString();
  const url = window.location.origin + '/events/' + e.id;
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Voucha//Events//EN', 'BEGIN:VEVENT', 'UID:voucha-event-' + e.id + '@thevoucha.com', 'DTSTAMP:' + stamp(new Date().toISOString()), 'DTSTART:' + stamp(e.startsAt), 'DTEND:' + stamp(end), 'SUMMARY:' + esc(e.title), 'DESCRIPTION:' + esc(e.description.slice(0, 900) + '\n\n' + url), 'LOCATION:' + esc(e.format === 'online' ? e.onlineUrl || 'Online' : [e.venue, e.address, e.placeName].filter(Boolean).join(', ')), 'URL:' + url, 'END:VEVENT', 'END:VCALENDAR'];
  return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(lines.join('\r\n'));
}
export function go(url) {
  return ev => {
    if (ev.ctrlKey || ev.metaKey || ev.shiftKey) return;
    ev.preventDefault();
    m.route.set(url);
  };
}

/** "Oct 28" and "Wednesday" for the timeline, in the event's time zone */
export function dayLabel(e) {
  return {
    date: parts(e.startsAt, e.timezone, {
      month: 'short',
      day: 'numeric'
    }).replace(/^(\d+) (\w+)$/, '$2 $1'),
    weekday: parts(e.startsAt, e.timezone, {
      weekday: 'long'
    })
  };
}

/** "7:00 PM" in the event's time zone */
export function clock(iso, tz) {
  return parts(iso, tz, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).toUpperCase();
}

/**
 * The cover: the uploaded picture, or Voucha's own pattern (always the same for one event): a deep colour with
 * an adire-style print of dots and stripes, and the date on a ticket.
 */
const COLOURS = ['#1b4540', '#8a3b22', '#2f3e75', '#8c5a12', '#5b2a4a', '#0f6262'];
const PRINTS = ['radial-gradient(circle, rgba(255,255,255,0.22) 1.6px, transparent 2.2px) 0 0 / 16px 16px, repeating-linear-gradient(45deg, rgba(255,255,255,0.07) 0 2px, transparent 2px 13px)', 'repeating-radial-gradient(circle at 0 0, rgba(255,255,255,0.12) 0 2px, transparent 2px 14px) 0 0 / 28px 28px', 'repeating-linear-gradient(0deg, rgba(255,255,255,0.08) 0 2px, transparent 2px 12px), repeating-linear-gradient(90deg, rgba(255,255,255,0.08) 0 2px, transparent 2px 12px)', 'radial-gradient(circle, rgba(255,255,255,0.16) 3px, transparent 3.5px) 0 0 / 22px 22px, radial-gradient(circle, rgba(255,255,255,0.1) 1.5px, transparent 2px) 11px 11px / 22px 22px'];
export function cover(e, cls) {
  if (e.image) return m('img', {
    className: cls,
    src: e.image,
    alt: '',
    loading: 'lazy'
  });
  const n = Number(e.id) || 0;
  const colour = COLOURS[n % COLOURS.length];
  const b = badge(e);
  return m('div', {
    className: cls + ' is-generated',
    style: {
      background: PRINTS[n % PRINTS.length] + ', ' + colour,
      color: colour
    }
  }, [m('span.VEvt-ticket', [m('small', b.month), m('b', b.date)])]);
}

/** One event as a timeline card: time, title, organiser, place, cover */
export function eventCard(e, extra) {
  const url = '/events/' + e.id;
  return m('a.VEvt-card', {
    href: url,
    onclick: go(url)
  }, [m('div.VEvt-cardText', [m('span.VEvt-cardTime', clock(e.startsAt, e.timezone) + (e.price ? ' · ' + e.price : '')), m('strong.VEvt-cardTitle', e.title), e.organiser ? m('span.VEvt-cardMeta', [m('i.far.fa-user', {
    'aria-hidden': 'true'
  }), ' By ' + e.organiser.name]) : null, m('span.VEvt-cardMeta', [m('i.' + (e.format === 'online' ? 'fas.fa-video' : 'fas.fa-location-dot'), {
    'aria-hidden': 'true'
  }), ' ' + whereText(e)]), extra || null]), cover(e, 'VEvt-cardCover')]);
}

/** Events grouped by day (in each event's time zone), for the timeline */
export function byDay(list) {
  const groups = [];
  list.forEach(e => {
    const d = dayLabel(e);
    const key = d.date + d.weekday;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.events.push(e);else groups.push({
      key,
      label: d,
      events: [e]
    });
  });
  return groups;
}

/** The timeline: day on the left, the day's events on the right */
export function timeline(list, extra) {
  return m('div.VEvt-timeline', byDay(list).map(g => m('section.VEvt-day', {
    key: g.key
  }, [m('div.VEvt-dayLabel', [m('strong', g.label.date), m('span', g.label.weekday)]), m('div.VEvt-dayEvents', g.events.map(e => eventCard(e, extra ? extra(e) : null)))])));
}

/** One event as a row: date badge, title, when, where */
export function eventRow(e, extra) {
  const b = badge(e);
  const url = '/events/' + e.id;
  return m('li.VEvt-row', {
    key: e.id
  }, [m('a.VEvt-rowLink', {
    href: url,
    onclick: go(url)
  }, [m('span.VEvt-badge', [m('small', b.day), m('b', b.date), m('small', b.month)]), m('span.VEvt-rowText', [m('strong', e.title), m('span', whenText(e)), m('span', [m('i.fas.' + (e.format === 'online' ? 'fa-video' : 'fa-location-dot'), {
    'aria-hidden': 'true'
  }), ' ', whereText(e)]), e.price ? m('span.VEvt-price', e.price) : null])]), extra || null]);
}
flarum.reg.add('voucha-community', 'forum/events/util', { EVENTS_ROUTE: EVENTS_ROUTE,EVENT_ROUTE: EVENT_ROUTE,NEW_EVENT_ROUTE: NEW_EVENT_ROUTE,api: api,onEvents: onEvents,onNewEvent: onNewEvent,onEvent: onEvent,onAnyEvents: onAnyEvents,badge: badge,whenText: whenText,localText: localText,whereText: whereText,icsHref: icsHref,go: go,dayLabel: dayLabel,clock: clock,cover: cover,eventCard: eventCard,byDay: byDay,timeline: timeline,eventRow: eventRow, });